import { Avatar } from "@/components/iris";
import { useUploadAvatar } from "@/hooks/use-users";
import type { Env } from "@/lib/types";
import { useRef, useState } from "react";

interface AvatarUploadProps {
  userId: string;
  currentUrl: string | null;
  username: string;
  env?: Env | undefined;
}

export function AvatarUpload({ userId, currentUrl, username, env }: AvatarUploadProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const upload = useUploadAvatar(env);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate client-side
    if (file.size > 2 * 1024 * 1024) {
      alert("File too large (max 2MB)");
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      alert("Invalid file type. Use JPEG, PNG, or WebP.");
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);

    // Upload
    try {
      await upload.mutateAsync({ id: userId, file });
    } catch {
      setPreview(null);
    }
  };

  const displayUrl = preview || currentUrl;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      <button
        type="button"
        style={{
          position: "relative",
          cursor: "pointer",
          background: "transparent",
          border: "none",
          padding: 0,
        }}
        onClick={() => fileRef.current?.click()}
      >
        {displayUrl ? (
          <img
            src={displayUrl}
            alt=""
            style={{ width: 56, height: 56, borderRadius: 999, objectFit: "cover" }}
          />
        ) : (
          <Avatar name={username || "?"} size={56} />
        )}
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 999,
            background: "rgba(0, 0, 0, 0.5)",
            opacity: 0,
            transition: "opacity 150ms ease-out",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.opacity = "1";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.opacity = "0";
          }}
        >
          <span style={{ fontSize: 10, fontWeight: 500, color: "var(--iris-fg-1)" }}>Change</span>
        </div>
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      {upload.isPending && (
        <span style={{ fontSize: 10, color: "var(--iris-fg-4)" }}>Uploading...</span>
      )}
    </div>
  );
}
